import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import PriorityOrderField from "@/components/PriorityOrderField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Coverage rule editor. Category mandatory, team optional.

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY = {
  category_id: "",
  name: "",
  description: "",
  priority: 100,
  is_active: true,
  days_of_week: [],
  start_time: "",
  end_time: "",
  min_headcount: null,
  max_headcount: null,
  min_coverage_pct: null,
  max_coverage_pct: null,
  team_id: "",
  effective_from: "",
  effective_to: "",
  location_id: "",
  apply_per_team: false,
};

export default function CoverageRuleEditor({ open, onOpenChange, editing, categories, teams, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => setLocations([]));
    if (editing) {
      setForm({
        category_id: editing.category_id || "",
        name: editing.name || "",
        description: editing.description || "",
        priority: editing.priority ?? 100,
        is_active: editing.is_active !== false,
        days_of_week: editing.days_of_week || [],
        start_time: editing.start_time || "",
        end_time: editing.end_time || "",
        min_headcount: editing.min_headcount ?? null,
        max_headcount: editing.max_headcount ?? null,
        min_coverage_pct: editing.min_coverage_pct ?? null,
        max_coverage_pct: editing.max_coverage_pct ?? null,
        team_id: editing.team_id || "",
        effective_from: editing.effective_from || "",
        effective_to: editing.effective_to || "",
        location_id: editing.location_id || "",
        apply_per_team: !!editing.apply_per_team,
      });
    } else { setForm(EMPTY); }
  }, [open, editing]);

  const set = (p) => setForm((prev) => ({ ...prev, ...p }));
  const toggleDay = (i) => {
    const s = new Set(form.days_of_week || []);
    if (s.has(i)) s.delete(i); else s.add(i);
    set({ days_of_week: Array.from(s).sort((a, b) => a - b) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.category_id) return toast.error("Category is required");
    setBusy(true);
    try {
      const body = {
        category_id: form.category_id,
        name: form.name.trim(),
        description: form.description || null,
        priority: Number(form.priority) || 100,
        is_active: form.is_active,
        days_of_week: form.days_of_week || [],
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        min_headcount: form.min_headcount ?? null,
        max_headcount: form.max_headcount ?? null,
        min_coverage_pct: form.min_coverage_pct ?? null,
        max_coverage_pct: form.max_coverage_pct ?? null,
        team_id: form.team_id || null,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        location_id: form.location_id || null,
        apply_per_team: !!form.apply_per_team,
      };
      if (editing) {
        await api.patch(`/coverage-rules/${editing.coverage_id}`, body);
        toast.success("Coverage policy updated");
      } else {
        await api.post("/coverage-rules", body);
        toast.success("Coverage policy created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  // Phase 2A Step 3 - Coverage Guard rules target Work categories
  // only in V1. Filter Away out of the picker so a manager cannot
  // accidentally create a rule against Break/Away. Rule schema and
  // backend unchanged - this is a UI guard only.
  const activeCategories = categories.filter(
    (c) => c.is_active !== false && c.kind === "work");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" fullscreenOnMobile data-testid="coverage-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit coverage policy" : "New coverage policy"}</DialogTitle>
          <DialogDescription>
            Set the minimum staff required for each category so shifts are properly
            covered. Optionally limit by team, location, day or time window.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="coverage-name"
                onChange={(e) => set({ name: e.target.value })}
                className="h-11 rounded-lg mt-2" placeholder="e.g. Weekday inbound cover" />
            </div>
            <div>
              <Label className="eyebrow">Category (required)</Label>
              <Select value={form.category_id} onValueChange={(v) => set({ category_id: v })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="coverage-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  {activeCategories.map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Team scope</Label>
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3">
                <label className="flex items-start gap-2 text-sm cursor-pointer"
                       data-testid="coverage-scope-company">
                  <input type="radio" name="cov-scope" className="mt-1"
                         checked={!form.team_id && !form.apply_per_team}
                         onChange={() => set({ team_id: "", apply_per_team: false })} />
                  <span><strong>Company-wide.</strong> Applies once across the company.</span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer"
                       data-testid="coverage-scope-team">
                  <input type="radio" name="cov-scope" className="mt-1"
                         checked={!!form.team_id && !form.apply_per_team}
                         onChange={() => set({ apply_per_team: false, team_id: form.team_id || (teams[0]?.team_id || "") })} />
                  <span><strong>Specific team.</strong> Applies to one team only.</span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer"
                       data-testid="coverage-scope-each-team">
                  <input type="radio" name="cov-scope" className="mt-1"
                         checked={!!form.apply_per_team}
                         onChange={() => set({ team_id: "", apply_per_team: true })} />
                  <span><strong>Each team.</strong> Evaluated separately for every team using that team's own employees. Saves you from creating one rule per team.</span>
                </label>
              </div>
              {!!form.team_id && !form.apply_per_team && (
                <Select value={form.team_id}
                  onValueChange={(v) => set({ team_id: v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="coverage-team"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {locations.length > 0 && (
              <div data-testid="coverage-location-row">
                <Label className="eyebrow">Location (optional)</Label>
                <Select value={form.location_id || "__all__"}
                  onValueChange={(v) => set({ location_id: v === "__all__" ? "" : v })}>
                  <SelectTrigger className="h-11 rounded-lg mt-2"
                    data-testid="coverage-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All locations</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.location_id} value={l.location_id}
                                   data-testid={`coverage-location-opt-${l.location_id}`}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <PriorityOrderField
                value={form.priority}
                onChange={(v) => set({ priority: v })}
                testid="coverage-priority" />
            </div>
            <div>
              <Label className="eyebrow">Status</Label>
              <Select value={form.is_active ? "active" : "archived"}
                onValueChange={(v) => set({ is_active: v === "active" })}>
                <SelectTrigger className="h-11 rounded-lg mt-2"
                  data-testid="coverage-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="eyebrow">Days of week</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {DAY_LABELS.map((d, i) => {
                const on = (form.days_of_week || []).includes(i);
                return (
                  <button type="button" key={d} onClick={() => toggleDay(i)}
                    data-testid={`coverage-day-${d}`}
                    className={`px-3 py-1 rounded-full border text-sm ${
                      on
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                    }`}>{d}</button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#4B5773] mt-1.5">
              Tick the days this requirement applies to. Leave all unticked to mean every day.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Start time</Label>
              <Input type="time" value={form.start_time} data-testid="coverage-start"
                onChange={(e) => set({ start_time: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">End time</Label>
              <Input type="time" value={form.end_time} data-testid="coverage-end"
                onChange={(e) => set({ end_time: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Minimum number of people</Label>
              <Input type="number" min="0" data-testid="coverage-min"
                value={form.min_headcount ?? ""}
                onChange={(e) => set({ min_headcount: e.target.value === "" ? null : Number(e.target.value) })}
                className="h-11 rounded-lg mt-2" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                The minimum number of qualified employees required.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Maximum number of people</Label>
              <Input type="number" min="0" data-testid="coverage-max"
                value={form.max_headcount ?? ""}
                onChange={(e) => set({ max_headcount: e.target.value === "" ? null : Number(e.target.value) })}
                className="h-11 rounded-lg mt-2" placeholder="No limit" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                Optional. Leave blank if there is no maximum.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Minimum coverage %</Label>
              <Input type="number" min="0" max="100" data-testid="coverage-min-pct"
                value={form.min_coverage_pct ?? ""}
                onChange={(e) => set({ min_coverage_pct: e.target.value === "" ? null : Number(e.target.value) })}
                className="h-11 rounded-lg mt-2" placeholder="e.g. 80" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                Optional. Minimum percentage of the scope team required.
                Combined with the headcount minimum using whichever is more
                permissive.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Maximum coverage %</Label>
              <Input type="number" min="0" max="100" data-testid="coverage-max-pct"
                value={form.max_coverage_pct ?? ""}
                onChange={(e) => set({ max_coverage_pct: e.target.value === "" ? null : Number(e.target.value) })}
                className="h-11 rounded-lg mt-2" placeholder="No limit" />
              <p className="text-[11px] text-[#4B5773] mt-1">
                Optional. Maximum percentage of the scope team that can cover.
              </p>
            </div>
            <div>
              <Label className="eyebrow">Effective from</Label>
              <Input type="date" value={form.effective_from} data-testid="coverage-eff-from"
                onChange={(e) => set({ effective_from: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
            <div>
              <Label className="eyebrow">Effective to</Label>
              <Input type="date" value={form.effective_to} data-testid="coverage-eff-to"
                onChange={(e) => set({ effective_to: e.target.value })}
                className="h-11 rounded-lg mt-2" />
            </div>
          </div>

          <div>
            <Label className="eyebrow">Description (optional)</Label>
            <Input value={form.description} data-testid="coverage-description"
              onChange={(e) => set({ description: e.target.value })}
              className="h-11 rounded-lg mt-2" />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="coverage-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : editing ? "Save changes" : "Create policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
